import type { RuleTopic } from '../vacationHelp.types';
import { MIN_ADVANCE_DAYS, MIN_CORRECTION_LEAD_DAYS } from '@/features/Vacations/utils/vacationStartDateRules';
import { SELL_DAYS, MIN_LONGEST_SEGMENT_DAYS, MIN_SEGMENT_DAYS, ENTITLED_DAYS_PER_PERIOD } from '@/features/Vacations/utils/vacationRules';
import { DRAFT_GRACE_MINUTES } from '@/features/Vacations/utils/vacationRequestStatus';
import { APPROVAL_LEAD_DAYS } from '@/features/Vacations/utils/vacationApprovalDeadline';
import { MONTHS_TO_REQUEST, MONTHS_TO_ENTITLEMENT } from '@/features/Vacations/utils/vacationEligibility';
import { EXPIRING_WINDOW_DAYS } from '@/features/Vacations/utils/panorama/constants';
import { SCHEDULE_LIMIT_MONTHS } from '@/features/Vacations/vacations.definitions';

export const REQUEST_RULES: RuleTopic[] = [
    {
        id: 'eligibility',
        icon: '✅',
        title: 'Direito e elegibilidade',
        summary: 'Entenda quando você poderá solicitar suas férias.',
        content: (
            <div className="space-y-3 text-sm">
                <p>
                    Você pode <strong>solicitar</strong> férias após <strong>{MONTHS_TO_REQUEST} meses</strong> de admissão. As férias só podem <strong>começar</strong> após <strong>{MONTHS_TO_ENTITLEMENT} meses</strong> completos.
                </p>
            </div>
        ),
        facts: [
            { label: 'Para solicitar', value: `${MONTHS_TO_REQUEST} meses` },
            { label: 'Para começar', value: `${MONTHS_TO_ENTITLEMENT} meses` },
            { label: 'Por período', value: `${ENTITLED_DAYS_PER_PERIOD} dias` },
        ],
    },
    {
        id: 'balance',
        icon: '💰',
        title: 'Seu saldo e prazos',
        summary: 'Entenda os números que aparecem no topo da página.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>Saldo disponível:</strong> total de dias que você ainda tem, contando os vencidos.</p>
                <p><strong>Dias vencidos:</strong> dias de férias que venceram porque o prazo para tirá-los já passou.</p>
                <p><strong>Limite p/ agendar até:</strong> última data para começar férias. Depois disso, os dias expiram.</p>
                <p><strong>Dias agendados:</strong> dias que você já pediu. Inclui os que ainda estão em aprovação.</p>
            </div>
        ),
    },
    {
        id: 'division',
        icon: '📊',
        title: 'Férias divididas',
        summary: 'Suas opções de divisão de férias.',
        content: (
            <div className="space-y-3 text-sm">
                <p>Você tem 3 opções:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                    <li><strong>{ENTITLED_DAYS_PER_PERIOD} dias corridos</strong> (tudo junto, sem divisão)</li>
                    <li><strong>10 + 20 dias</strong> (escolha a ordem: 10 primeiro ou 20 primeiro)</li>
                    <li><strong>15 + 15 dias</strong> (dois períodos iguais)</li>
                </ul>
                <p className="text-gray-600 text-xs mt-2">
                    Nas modalidades divididas, você escolhe o início do 2º período.
                </p>
            </div>
        ),
        facts: [
            { label: 'Opção 1', value: '30 dias' },
            { label: 'Opção 2', value: '10 + 20 dias' },
            { label: 'Opção 3', value: '15 + 15 dias' },
        ],
    },
    {
        id: 'selling',
        icon: '💵',
        title: `Abono pecuniário (${SELL_DAYS} dias)`,
        summary: 'Manifestar interesse em converter parte das férias em abono.',
        content: (
            <div className="space-y-3 text-sm">
                <p>Você pode <strong>manifestar interesse em converter 1/3 dos dias</strong> em abono pecuniário.</p>
                <p>Ao marcar: suas férias viram <strong>{ENTITLED_DAYS_PER_PERIOD - SELL_DAYS} dias corridos</strong> (sem divisão). Se o Departamento Pessoal reprovar o abono, os {SELL_DAYS} dias voltam ao saldo.</p>
                <p className="font-semibold text-amber-700">
                    ⚠️ Só aparece na modalidade <strong>30 dias com saldo completo</strong>.
                </p>
            </div>
        ),
        facts: [
            { label: 'Para converter', value: `${SELL_DAYS} dias` },
            { label: 'Você goza', value: `${ENTITLED_DAYS_PER_PERIOD - SELL_DAYS} dias` },
            { label: 'Divisão', value: 'Não' },
        ],
        callout: 'É uma manifestação de interesse, não uma venda. Quem decide é o Departamento Pessoal, junto com a validação do pedido.',
    },
    {
        id: 'startdate',
        icon: '📅',
        title: 'Data de início',
        summary: 'Regras para quando suas férias podem começar.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>Dia da semana:</strong> apenas segunda, terça ou quarta.</p>
                <p><strong>Feriados:</strong> não pode ser no feriado nem nos <strong>2 dias antes</strong> dele.</p>
                <p><strong>Antecedência:</strong> o início precisa ser daqui a, no mínimo, <strong>{MIN_ADVANCE_DAYS} dias</strong>.</p>
                <p><strong>Limite:</strong> o agendamento fica disponível até a data de "Limite p/ agendar até". Depois dela, só solicitando diretamente ao Departamento Pessoal.</p>
            </div>
        ),
        facts: [
            { label: 'Antecedência mín.', value: `${MIN_ADVANCE_DAYS} dias` },
            { label: 'Dias da semana', value: 'Seg, Ter, Qua' },
            { label: 'Antes de feriado', value: '2 dias bloq.' },
        ],
    },
    {
        id: 'lifecycle',
        icon: '🔄',
        title: 'Fluxo da solicitação',
        summary: 'O caminho do seu pedido até as férias.',
        content: (
            <div className="space-y-3 text-sm">
                <p><strong>Aguardando envio ao gestor:</strong> depois de confirmar, seu pedido espera {DRAFT_GRACE_MINUTES} minutos antes de seguir. Nesse tempo, você pode excluir a solicitação ou enviá-la antes.</p>
                <p><strong>Aprovações:</strong> a aprovação passa por níveis. Primeiro o gestor e o cliente, se houver. Depois, o Departamento Pessoal faz a validação final.</p>
                <p><strong>Prazo:</strong> se o pedido não for aprovado até {APPROVAL_LEAD_DAYS} dias antes do início, ele é reprovado automaticamente.</p>
                <p><strong>Correção:</strong> se o Departamento Pessoal ou o cliente recusar algum período, você escolhe novas datas, com pelo menos {MIN_CORRECTION_LEAD_DAYS} dias de antecedência.</p>
                <p className="text-gray-600 text-xs mt-2">
                    Você só pode ter um pedido em andamento por vez, contando as férias agendadas.
                </p>
            </div>
        ),
        example: `Você confirma o pedido. Por ${DRAFT_GRACE_MINUTES} minutos ele fica em "Aguardando envio ao gestor" e depois segue sozinho.`,
    },
];
